import { useEffect, useRef } from 'react';

/**
 * Reintento del drenado con retroceso (2026-09-08).
 *
 * El banner drenaba al cambiar `isOnline`, al arrancar, al encolar un lote de
 * fotos, al volver a primer plano y con el botón. Ninguno cubre «se encoló con
 * la app ONLINE y sigue online» — la red degradada: la mutation venció su techo
 * de espera, se encoló, y nada la volvía a intentar hasta un corte de red, un
 * cambio de pestaña o el botón. Con retroceso (15 s → 5 min) el trabajo acaba
 * subiendo solo sin machacar una red que ya va mal.
 *
 * Por qué el primer disparo es a 15 s y no inmediato: el encolado de un lote de
 * fotos entra una a una y drenar a mitad deja fotos fuera (ver el comentario del
 * drenado al arrancar en `OfflineBanner`); y con red degradada, reintentar en
 * el acto es repetir el timeout.
 */
export const DRAIN_BACKOFF_MS = [15_000, 30_000, 60_000, 120_000, 300_000] as const;

/** Retardo del intento `attempt` (0 = primero), acotado al último valor. */
export function nextDrainDelayMs(attempt: number): number {
  const index = Math.min(Math.max(attempt, 0), DRAIN_BACKOFF_MS.length - 1);
  return DRAIN_BACKOFF_MS[index];
}

export function useDrainScheduler({
  active,
  drain,
}: {
  /** Hay pendientes, hay red y no se está sincronizando ya. */
  active: boolean;
  /** El drenado; si sincroniza algo, el retroceso vuelve al principio. */
  drain: () => Promise<{ synced: number } | void>;
}): void {
  const attemptRef = useRef(0);
  const drainRef = useRef(drain);
  drainRef.current = drain;

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const arm = (): void => {
      timer = setTimeout(async () => {
        if (cancelled) return;
        const result = await drainRef.current();
        // La contabilidad del intento va SIEMPRE, aunque el efecto se haya
        // cancelado entre medias: el banner pone `isSyncing` mientras drena,
        // eso apaga `active` y cancela este efecto; si el contador solo
        // subiera con el efecto vivo, cada re-armado partiría de 0 y los
        // reintentos serían cada 15 s fijos (medido con el proxy 2026-09-08).
        attemptRef.current =
          result && result.synced > 0 ? 0 : attemptRef.current + 1;
        if (cancelled) return;
        arm();
      }, nextDrainDelayMs(attemptRef.current));
    };
    arm();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [active]);
}
